/**
 * InstanceBadge.jsx — HA Warm-Standby Health Indicator
 * Small floating pill in bottom-left corner showing which server serves the request.
 * - Green = MAIN (Server 1)
 * - Amber = STANDBY (App VM2)
 */
import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const InstanceBadge = () => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchInfo = () => {
      fetch(`${BACKEND_URL}/api/public/instance-info`, { cache: 'no-store' })
        .then(r => r.ok ? r.json() : null)
        .then(j => { if (mounted && j?.data) setInfo(j.data); })
        .catch(() => {});
    };
    fetchInfo();
    const iv = setInterval(fetchInfo, 30000); // refresh every 30s
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  if (!info) return null;

  const isMain = info.role === 'main';
  const label = isMain ? 'MAIN' : 'STANDBY';
  const bgColor = isMain
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
  const glowColor = isMain ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.4)';

  return (
    <div
      data-testid="ha-instance-badge"
      title={`${info.role.toUpperCase()} · ${info.instance_id} · ${info.hostname}`}
      style={{
        position: 'fixed',
        top: '78px',
        right: '60px',
        zIndex: 9998,
        padding: '3px 8px',
        borderRadius: '999px',
        background: bgColor,
        color: '#fff',
        fontSize: '9px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        boxShadow: `0 2px 8px ${glowColor}, 0 0 0 1px rgba(255,255,255,0.15) inset`,
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        cursor: 'default',
        userSelect: 'none',
        opacity: 0.75,
        transition: 'opacity 0.2s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.85'}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 0 4px #fff',
          animation: 'ha-pulse 2s ease-in-out infinite',
        }}
      />
      {label}
      <span style={{ opacity: 0.7, fontWeight: 500 }}>· {info.instance_id}</span>
      <style>{`
        @keyframes ha-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
};

export default InstanceBadge;
